import React, { useEffect, useRef, useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import api from '../services/api';
import type StudyParticipantModel from '../models/StudyParticipantModel';
import type StudyModel from '../models/StudyModel';
import type IsoDate from '../models/IsoDate';
import Table from './Table';
import CreateStudyParticipantForm from './CreateStudyParticipantForm';
import StudyDocuments from './StudyDocuments';
import StudyAnswers from './StudyAnswers';

type PropTypes = {
    study?: StudyModel;
};

const StudyParticipants = ({ study }: PropTypes) => {
    const [participants, setParticipants] = useState<StudyParticipantModel[]>(
        [],
    );
    const [selectedParticipant, setSelectedParticipant] = useState<
        StudyParticipantModel | undefined
    >(undefined);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [addingParticipant, setAddingParticipant] = useState(false);

    const toast = useRef<Toast>(null);

    const pullParticipants = async () => {
        const { participants, success } = await api.getStudyParticipants(
            study?.id,
        );
        if (success) {
            setParticipants(participants);
        }
    };

    useEffect(() => {
        void pullParticipants();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setStartDate(selectedParticipant?.start_date ?? '');
        setEndDate(selectedParticipant?.end_date ?? '');
    }, [selectedParticipant]);

    const editParticipant = async (
        id: string,
        startDate: IsoDate,
        endDate: IsoDate,
    ) => {
        const { success, message } = await api.editStudyParticipant(
            id,
            startDate,
            endDate,
        );
        if (success) {
            toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Participant info updated',
            });
        } else {
            toast.current?.show({
                severity: 'warn',
                summary: 'Update Failed',
                detail: message ?? 'unknown error',
            });
        }
    };

    const deleteParticipant = async (id: string) => {
        const { success } = await api.deleteStudyParticipant(id);
        if (success) {
            setSelectedParticipant(undefined);
            void pullParticipants();
        }
    };

    const onParticipantCreated = () => {
        setAddingParticipant(false);
        void pullParticipants();
    };

    const columns = ['id', 'study_id', 'user_id', 'start_date', 'end_date'];

    return (
        <div>
            <Toast ref={toast} position="top-right" />
            <div className="flex items-center">
                <div className="text-primary text-3xl">Study Participants</div>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                        setAddingParticipant(true);
                    }}
                >
                    Add Participant
                </button>
            </div>
            <Table
                title="Participants"
                data={participants}
                columns={columns}
                onClick={(study) => {
                    setSelectedParticipant(study as StudyParticipantModel);
                }}
            />

            {selectedParticipant !== undefined && (
                <Dialog
                    visible={selectedParticipant !== undefined}
                    header={`Study Participant ${selectedParticipant.id}`}
                    onHide={() => {
                        setSelectedParticipant(undefined);
                    }}
                >
                    <div className="flex items-center mb-4">
                        <div className="text-primary text-3xl">
                            Participation Dates
                        </div>
                    </div>
                    <div className="flex flex-row items-center gap-4">
                        <label>Start Date</label>
                        <InputText
                            value={startDate}
                            onChange={(event) => {
                                setStartDate(event.target.value);
                            }}
                        />
                        <label>End Date</label>
                        <InputText
                            value={endDate}
                            onChange={(event) => {
                                setEndDate(event.target.value);
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-primary ml-4"
                            onClick={async () => {
                                await editParticipant(
                                    selectedParticipant.id,
                                    startDate,
                                    endDate,
                                );
                            }}
                        >
                            Save
                        </button>
                    </div>
                    <StudyAnswers
                        study={study}
                        participant={selectedParticipant}
                    />
                    <StudyDocuments participant={selectedParticipant} />
                    <a
                        href={`/studies/${selectedParticipant.study_id}/participants/${selectedParticipant.id}/report`}
                        className="btn btn-primary mt-4 mr-4"
                    >
                        View Report
                    </a>
                    <button
                        type="button"
                        className="btn btn-primary mt-4"
                        onClick={() => {
                            confirmDialog({
                                message: `Are you sure you want to delete participant ${selectedParticipant.id}?`,
                                accept: async () =>
                                    deleteParticipant(selectedParticipant.id),
                            });
                        }}
                    >
                        Delete Participant
                    </button>
                </Dialog>
            )}
            {addingParticipant && (
                <Dialog
                    visible={addingParticipant}
                    header="Add Participant"
                    onHide={() => {
                        setAddingParticipant(false);
                    }}
                >
                    <CreateStudyParticipantForm
                        study={study}
                        onParticipantCreated={onParticipantCreated}
                    />
                </Dialog>
            )}
        </div>
    );
};

export default StudyParticipants;
